import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, LayoutList, Percent, Plus, ShieldAlert, TrendingUp, Truck, X } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { editProduct, useMenuCategoryOptions } from '@/hooks/useInventory';
import { formatCurrencyExact } from '@/lib/currency';

// Delivery marketplaces that carry per-product pricing and categorisation.
// Mirrors the channels handled by the retailers dashboard's DeliveryChannelsCard.
const DELIVERY_CHANNELS = [
	{ id: 'deliveroo', label: 'Deliveroo' },
	{ id: 'uber-eats', label: 'Uber Eats' },
	{ id: 'just-eat', label: 'Just Eat' }
];

// Restricted-product classifications — mirrors the enum in
// @nearst/base-product-data (and custom-product-data/lib/classifications.json)
// so the values pass server-side validation. Add new values to all three.
const CLASSIFICATIONS = [
	{ id: 'early_stage_infant_formula', title: 'Infant formula' },
	{ id: 'pharmaceuticals_aspirin', title: 'Aspirin' },
	{ id: 'pharmaceuticals_ibuprofen', title: 'Ibuprofen' },
	{ id: 'pharmaceuticals_paracetamol', title: 'Paracetamol' },
	{ id: 'alcohol_product', title: 'Alcohol (18+)' },
	{ id: 'vape_product', title: 'Vape (18+)' },
	{ id: 'tobacco_product', title: 'Tobacco (18+)' },
	{ id: 'nicotine_product', title: 'Nicotine (18+)' },
	{ id: 'cbd_product', title: 'CBD' }
];

const INPUT_CLASS =
	'w-full h-11 px-3.5 rounded-lg border border-border bg-white text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

function PercentInput({ value, onChange, id }) {
	return (
		<div className="relative">
			<input
				id={id}
				type="number"
				min="0"
				max="100"
				inputMode="decimal"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className={`${INPUT_CLASS} pr-8`}
			/>
			<span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
		</div>
	);
}

/**
 * A creatable category picker — lists the aisles / shelves the shop already
 * uses (faceted from its inventory) and lets the retailer type a new one.
 * Mirrors the retailers dashboard's react-select Creatable behaviour.
 */
function CategoryCombobox({ value, onChange, options, placeholder, disabled, emptyHint }) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');

	const normalised = query.trim().toLowerCase();
	const filtered = options.filter((o) => o.label.toLowerCase().includes(normalised));
	const exactMatch = options.some((o) => o.label.toLowerCase() === normalised);
	const canCreate = query.trim() !== '' && !exactMatch;

	const choose = (next) => {
		onChange(next);
		setQuery('');
		setOpen(false);
	};

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				if (disabled) return;
				setOpen(next);
				if (!next) setQuery('');
			}}
		>
			<PopoverTrigger asChild>
				<button
					type="button"
					disabled={disabled}
					className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-border bg-white px-3.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-muted/40"
				>
					<span className={value ? 'truncate text-foreground' : 'truncate text-muted-foreground'}>{value || placeholder}</span>
					{value && !disabled ? (
						<X
							className="h-4 w-4 shrink-0 text-muted-foreground transition hover:text-foreground"
							onClick={(e) => {
								e.stopPropagation();
								choose('');
							}}
						/>
					) : (
						<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
					)}
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-[--radix-popover-trigger-width] !p-0">
				<div className="border-b border-border/60 p-2">
					<input
						autoFocus
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Search or add…"
						className="w-full rounded-md bg-muted/50 px-2.5 py-1.5 text-sm outline-none"
					/>
				</div>
				<div className="max-h-56 overflow-y-auto p-1.5">
					{filtered.map((o) => (
						<button
							key={o.value}
							type="button"
							onClick={() => choose(o.value)}
							className="flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition hover:bg-accent"
						>
							<span className="truncate">{o.label}</span>
							{value === o.value ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> : null}
						</button>
					))}
					{canCreate ? (
						<button
							type="button"
							onClick={() => choose(query.trim())}
							className="flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-sm font-semibold text-primary transition hover:bg-accent"
						>
							<Plus className="h-3.5 w-3.5 shrink-0" /> Use “{query.trim()}”
						</button>
					) : null}
					{!filtered.length && !canCreate ? (
						<p className="px-2.5 py-3 text-center text-xs text-muted-foreground">{emptyHint}</p>
					) : null}
				</div>
			</PopoverContent>
		</Popover>
	);
}

function Section({ label, hint, icon: Icon, children }) {
	return (
		<div>
			<div className="flex items-center gap-2 text-sm font-semibold text-foreground">
				<span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
					<Icon className="h-3.5 w-3.5" />
				</span>
				{label}
			</div>
			{hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
			<div className="mt-2.5">{children}</div>
		</div>
	);
}

/**
 * Per-product delivery settings — price markup per marketplace, tax rate,
 * menu category (aisle / shelf) and restricted-product classifications.
 * Only the delivery channels this item is actually on get a markup field.
 *
 * Renders nothing unless the item is on at least one delivery channel.
 */
export function DeliveryChannelsCard({ record, shopId, onSaved }) {
	const enabledDeliveryChannels = useMemo(
		() => DELIVERY_CHANNELS.filter((c) => record?.channels && c.id in record.channels),
		[record?.channels]
	);

	const [markup, setMarkup] = useState({});
	const [tax, setTax] = useState('');
	const [aisle, setAisle] = useState('');
	const [shelf, setShelf] = useState('');
	const [classifications, setClassifications] = useState([]);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [saved, setSaved] = useState(false);
	const [open, setOpen] = useState(false);

	const { aisleOptions, shelfOptions } = useMenuCategoryOptions(shopId, aisle);

	useEffect(() => {
		if (!record) return;
		const initialMarkup = {};
		for (const [id, value] of Object.entries(record.deliveryMarkup ?? {})) {
			initialMarkup[id] = value == null ? '' : String(value);
		}
		setMarkup(initialMarkup);
		setTax(record.taxPercentage == null ? '' : String(record.taxPercentage));
		const [recordAisle = '', recordShelf = ''] = record.deliveryCategories ?? [];
		setAisle(recordAisle);
		setShelf(recordShelf);
		setClassifications(record.classifications ?? []);
	}, [record]);

	if (!record || enabledDeliveryChannels.length === 0) return null;

	const channelNames = enabledDeliveryChannels.map((c) => c.label).join(', ');

	const toggleClassification = (id) => {
		setSaved(false);
		setClassifications((current) => (current.includes(id) ? current.filter((c) => c !== id) : [...current, id]));
	};

	const setMarkupFor = (id) => (value) => {
		setSaved(false);
		setMarkup((current) => ({ ...current, [id]: value }));
	};

	const onSave = async () => {
		if (!shopId) return;
		setSaving(true);
		setError(null);
		setSaved(false);
		try {
			const deliveryMarkup = {};
			for (const channel of enabledDeliveryChannels) {
				const raw = markup[channel.id];
				if (raw !== '' && raw != null && Number.isFinite(Number(raw))) {
					deliveryMarkup[channel.id] = Number(raw);
				}
			}
			const patch = {
				deliveryCategories: [aisle.trim(), shelf.trim()].filter(Boolean),
				classifications
			};
			if (Object.keys(deliveryMarkup).length) patch.deliveryMarkup = deliveryMarkup;
			if (tax !== '' && Number.isFinite(Number(tax))) patch.taxPercentage = Number(tax);

			await editProduct(shopId, record.barcode, patch);
			await onSaved?.();
			setSaved(true);
		} catch (e) {
			setError(e?.response?.data?.message ?? e.message ?? 'Something went wrong saving these settings.');
		} finally {
			setSaving(false);
		}
	};

	const price = Number(record.price);
	const hasPrice = Number.isFinite(price) && price > 0;

	return (
		<SoftCard className="!p-5">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
				className="flex w-full items-start justify-between gap-3 text-left"
			>
				<div>
					<div className="flex items-center gap-2">
						<h3 className="text-base font-bold text-foreground">Selling on delivery apps</h3>
						<span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
							<Truck className="h-3.5 w-3.5" />
						</span>
					</div>
					<p className="mt-1 text-sm text-muted-foreground">
						Set the price, tax and menu placement for this product on {channelNames}.
					</p>
				</div>
				<ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
			</button>

			{!open ? null : (
				<>
					<div className="mt-5 space-y-6 border-t border-border/60 pt-5">
						<Section
							label="Price markup"
							icon={TrendingUp}
							hint="An uplift on this product's shelf price to cover delivery commission and fees."
						>
							<div className="space-y-4">
								{enabledDeliveryChannels.map((channel) => {
									const raw = markup[channel.id];
									const pct = raw === '' || raw == null ? 0 : Number(raw);
									const preview = hasPrice && Number.isFinite(pct) ? price * (1 + pct / 100) : null;
									return (
										<div key={channel.id}>
											<label
												htmlFor={`markup-${channel.id}`}
												className="mb-1.5 block text-xs font-semibold text-muted-foreground"
											>
												{channel.label}
											</label>
											<PercentInput
												id={`markup-${channel.id}`}
												value={raw ?? ''}
												onChange={setMarkupFor(channel.id)}
											/>
											{preview != null ? (
												<p className="mt-1.5 text-xs text-muted-foreground">
													Customer pays ≈{' '}
													<span className="font-semibold text-foreground tabular-nums">
														{formatCurrencyExact(preview, record.currency)}
													</span>
												</p>
											) : null}
										</div>
									);
								})}
							</div>
						</Section>

						<Section label="Tax rate" icon={Percent} hint="VAT or sales tax applied to this product on delivery channels.">
							<div>
								<PercentInput
									id="delivery-tax"
									value={tax}
									onChange={(value) => {
										setSaved(false);
										setTax(value);
									}}
								/>
							</div>
						</Section>

						<Section
							label="Menu categories"
							icon={LayoutList}
							hint="Where this product sits in your delivery storefront — aisle, then shelf."
						>
							<div className="space-y-4">
								<div>
									<div className="mb-1.5 text-xs font-semibold text-muted-foreground">Aisle</div>
									<CategoryCombobox
										value={aisle}
										onChange={(next) => {
											setSaved(false);
											setAisle(next);
											setShelf('');
										}}
										options={aisleOptions}
										placeholder="Select or add an aisle"
										emptyHint="No aisles in use yet — type to add one"
									/>
								</div>
								<div>
									<div className="mb-1.5 text-xs font-semibold text-muted-foreground">Shelf</div>
									<CategoryCombobox
										value={shelf}
										onChange={(next) => {
											setSaved(false);
											setShelf(next);
										}}
										options={shelfOptions}
										placeholder={aisle ? 'Select or add a shelf' : 'Choose an aisle first'}
										disabled={!aisle.trim()}
										emptyHint="No shelves under this aisle yet — type to add one"
									/>
								</div>
							</div>
						</Section>

						<Section
							label="Restricted-product classifications"
							icon={ShieldAlert}
							hint="Flag age-restricted or regulated products so delivery channels apply the right rules."
						>
							<div className="flex flex-wrap gap-2">
								{CLASSIFICATIONS.map((c) => {
									const selected = classifications.includes(c.id);
									return (
										<button
											key={c.id}
											type="button"
											onClick={() => toggleClassification(c.id)}
											className={
												selected
													? 'inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition'
													: 'inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:bg-accent'
											}
										>
											{selected ? <Check className="h-3 w-3" /> : null}
											{c.title}
										</button>
									);
								})}
							</div>
						</Section>
					</div>

					<div className="mt-6 flex items-center gap-3">
						<button
							type="button"
							onClick={onSave}
							disabled={saving}
							className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
						>
							{saving ? 'Saving…' : 'Save delivery settings'}
						</button>
						{saved ? (
							<span className="inline-flex items-center gap-1.5 text-sm font-semibold text-success-soft-foreground">
								<Check className="h-4 w-4" /> Saved
							</span>
						) : null}
					</div>
					{error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
				</>
			)}
		</SoftCard>
	);
}
